import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, Typography, Button } from '@mui/material';
import axios from 'axios';
import { BASE_URL } from '../../constants/config';
import { colors, fontFamilies } from '../../constants/theme';
import { registerSimplePracticeGate, unregisterSimplePracticeGate } from '../../utils/simplePractice';
import { loadTurnstile } from './turnstileLoader';

// Cloudflare's documented "always passes" test key, so the flow is usable
// before the real key exists. Never ships as the production value — set
// REACT_APP_TURNSTILE_SITE_KEY at build time.
const TEST_SITE_KEY = '1x00000000000000000000AA';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || TEST_SITE_KEY;

/**
 * Human-verification step between the booking buttons and SimplePractice.
 *
 * Mounted once, near the router. Every openSimplePractice*() call anywhere on
 * the site routes through here: challenge first, then the backend hands back
 * the SimplePractice link, then the visitor is sent there in a new tab.
 */
const SimplePracticeGate = () => {
  const [request, setRequest] = useState(null); // { type, practitioner }
  const [status, setStatus] = useState('idle'); // idle | loading | ready | verifying | blocked | error
  const [error, setError] = useState('');
  // Set only when the browser refused to open the tab for us — the visitor
  // finishes with a click of their own, which no popup blocker can stop.
  const [blockedUrl, setBlockedUrl] = useState('');
  // Bumped by "Try again" so the render effect runs a second time; `request`
  // alone does not change on a retry.
  const [attempt, setAttempt] = useState(0);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  const close = useCallback(() => {
    setRequest(null);
    setStatus('idle');
    setError('');
    setBlockedUrl('');
  }, []);

  const handleRequest = useCallback((payload) => {
    setError('');
    setBlockedUrl('');
    setStatus('loading');
    setAttempt(0);
    setRequest(payload);
  }, []);

  useEffect(() => {
    registerSimplePracticeGate(handleRequest);
    return () => unregisterSimplePracticeGate(handleRequest);
  }, [handleRequest]);

  const exchangeToken = useCallback(
    async (token, payload) => {
      setStatus('verifying');
      try {
        const { data } = await axios.post(`${BASE_URL}/simple-practice/link`, {
          type: payload.type,
          practitioner: payload.practitioner,
          token,
        });
        if (!data?.url) throw new Error('No link returned');

        const win = window.open(data.url, '_blank', 'noopener,noreferrer');
        if (win) {
          setRequest(null);
          setStatus('idle');
        } else {
          setBlockedUrl(data.url);
          setStatus('blocked');
        }
      } catch (e) {
        setError(
          e.response?.data?.message ||
            'We could not open the booking page. Please try again in a moment.'
        );
        setStatus('error');
      }
    },
    []
  );

  // Renders the widget once per open (and once per retry). `status` must stay
  // out of the dependencies: it changes while the challenge is on screen, and
  // the cleanup below would tear the freshly rendered widget straight back down.
  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current) return;
        widgetIdRef.current = turnstile.render(widgetRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => exchangeToken(token, request),
          'error-callback': () => {
            setError('Verification could not be completed. Please try again.');
            setStatus('error');
          },
          'expired-callback': () => {
            setError('The verification expired. Please try again.');
            setStatus('error');
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setError('The verification widget could not be loaded. Please refresh and try again.');
        setStatus('error');
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [request, attempt, exchangeToken]);

  const retry = () => {
    setError('');
    setBlockedUrl('');
    setStatus('loading');
    setAttempt((n) => n + 1);
  };

  const showWidget = status === 'loading' || status === 'ready';

  return (
    <Dialog open={!!request} onClose={close} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
        <Typography
          sx={{ fontFamily: fontFamilies.gotham, fontSize: 20, color: colors.primary, mb: 1 }}
        >
          Quick security check
        </Typography>
        <Typography
          sx={{ fontFamily: fontFamilies.quicksand, fontSize: 14, color: colors.textLight, mb: 3 }}
        >
          This helps us keep practitioner calendars free for real clients. It only takes a moment.
        </Typography>

        {/* Kept mounted while the challenge runs — Turnstile renders into it. */}
        <Box
          ref={widgetRef}
          sx={{ display: showWidget ? 'flex' : 'none', justifyContent: 'center', minHeight: 65 }}
        />

        {(status === 'loading' || status === 'verifying') && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} sx={{ color: colors.secondary }} />
            <Typography sx={{ fontFamily: fontFamilies.quicksand, fontSize: 14, color: colors.textLight }}>
              {status === 'verifying' ? 'Opening SimplePractice…' : 'Loading…'}
            </Typography>
          </Box>
        )}

        {status === 'blocked' && (
          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{ fontFamily: fontFamilies.quicksand, fontSize: 14, color: colors.textLight, mb: 2 }}
            >
              You&apos;re verified. Your browser blocked the new tab, so open your booking page here.
            </Typography>
            <Button
              variant="contained"
              component="a"
              href={blockedUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              sx={{ textTransform: 'none' }}
            >
              Continue to booking
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: fontFamilies.quicksand, fontSize: 14, color: '#B23A48', mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={retry} sx={{ textTransform: 'none', mr: 1 }}>
              Try again
            </Button>
            <Button onClick={close} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SimplePracticeGate;
